import { useUser } from "@components/user-context";
import { useNorthbeamMutation, useNorthbeamMutationPromise } from "@hooks/http";
import {
  type UnadoptBudgetMix,
  type UnadoptBudgetMixVariables,
  type AdoptBudgetMix,
  type AdoptBudgetMixVariables,
  type BlessBudgetMix,
  type BlessBudgetMixVariables,
  type DeleteBudgetMix,
  type DeleteBudgetMixVariables,
} from "@north-beam/nb-client-side";
import { BUDGET_MIXES_FOR_CYCLE_QUERY } from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import {
  ADOPT_BUDGET_MIX,
  BLESS_BUDGET_MIX,
  DELETE_BUDGET_MIX,
  UNADOPT_BUDGET_MIX,
} from "../adopt-mix-mutation";
import { useMMMAdminMode } from "../budget-mix-table/utils";

export const useBudgetMix = () => {
  const { selectedCycle } = useSelectedCycle();

  const [blessBudgetMix, { loading: blessBudgetMixLoading }] =
    useNorthbeamMutation<BlessBudgetMix, BlessBudgetMixVariables>(
      BLESS_BUDGET_MIX,
    );
  const user = useUser();
  const isAdminMode = useMMMAdminMode(user.user);

  const [
    adoptBudgetMix,
    { loading: loadingAdoptBudgetMix, error: adoptError },
  ] = useNorthbeamMutationPromise<AdoptBudgetMix, AdoptBudgetMixVariables>(
    ADOPT_BUDGET_MIX,
    {
      doNotRaiseErrorOnFailure: true,
      refetchQueries: [
        {
          query: BUDGET_MIXES_FOR_CYCLE_QUERY,
          variables: { id: selectedCycle.id, blessed: !isAdminMode },
        },
      ],

      awaitRefetchQueries: true,
    },
  );

  const [
    unadoptBudgetMix,
    { loading: loadingUnAdoptBudgetMix, error: unadoptError },
  ] = useNorthbeamMutationPromise<UnadoptBudgetMix, UnadoptBudgetMixVariables>(
    UNADOPT_BUDGET_MIX,
    {
      doNotRaiseErrorOnFailure: true,
      refetchQueries: [
        {
          query: BUDGET_MIXES_FOR_CYCLE_QUERY,
          variables: { id: selectedCycle.id, blessed: !isAdminMode },
        },
      ],

      awaitRefetchQueries: true,
    },
  );

  const [deleteBudgetMix, { loading: deleteBudgetMixLoading }] =
    useNorthbeamMutation<DeleteBudgetMix, DeleteBudgetMixVariables>(
      DELETE_BUDGET_MIX,
      {
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: BUDGET_MIXES_FOR_CYCLE_QUERY,
            variables: {
              id: selectedCycle.id,
            },
          },
        ],
      },
    );

  return {
    blessBudgetMix,
    blessBudgetMixLoading,
    adoptBudgetMix,
    loadingAdoptBudgetMix,
    adoptError,
    unadoptBudgetMix,
    loadingUnAdoptBudgetMix,
    unadoptError,
    deleteBudgetMix,
    deleteBudgetMixLoading,
  };
};
